import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { KindIcon } from '@/components/kind-icon';
import { api, type TrendForKind } from '@/lib/api';
import { cn } from '@/lib/cn';

export const Route = createFileRoute('/')({
  component: Dashboard,
});

const WINDOWS = [7, 14, 28] as const;
type Window = (typeof WINDOWS)[number];

const KIND_LABEL: Record<TrendForKind['kind'], string> = {
  ectopic: 'Ectopic',
  reflux: 'Reflux',
  ibs: 'IBS',
  pain: 'Pain',
  overall: 'Overall',
};

function Dashboard() {
  const [windowDays, setWindowDays] = React.useState<Window>(7);
  const whoami = useQuery({ queryKey: ['whoami'], queryFn: api.whoami });
  const trends = useQuery({
    queryKey: ['trends', windowDays],
    queryFn: () => api.getTrends(windowDays),
    enabled: whoami.data?.logged_in === true,
    refetchOnWindowFocus: true,
  });

  const overall = trends.data?.trends.find((t) => t.kind === 'overall');

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
        <p className="text-sm text-muted-foreground">
          Trending worse or better? Comparing the last {windowDays} days to the {windowDays}{' '}
          before that.
        </p>
        <div className="flex gap-1">
          {WINDOWS.map((w) => (
            <Button
              key={w}
              size="sm"
              variant={w === windowDays ? 'default' : 'outline'}
              onClick={() => setWindowDays(w)}
            >
              {w}d
            </Button>
          ))}
        </div>
      </section>

      {whoami.data && !whoami.data.logged_in && (
        <Card>
          <CardHeader>
            <CardTitle>Not signed in</CardTitle>
            <CardDescription>
              {whoami.data.passkeys_registered > 0
                ? 'Sign in on the Settings page to see your trends.'
                : 'Register a passkey on the Settings page to start logging.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild variant="default">
              <Link to="/settings">Open settings</Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {whoami.data?.logged_in && (
        <>
          <OverallHero trend={overall} loading={trends.isPending} />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {trends.data?.trends
              .filter((t) => t.kind !== 'overall')
              .map((t) => <KindCard key={t.kind} trend={t} />)}
            {trends.isPending && !trends.data && (
              <>
                <SkeletonCard />
                <SkeletonCard />
                <SkeletonCard />
                <SkeletonCard />
              </>
            )}
          </div>
          {trends.error && (
            <p className="text-sm text-destructive">
              {(trends.error as Error).message}
            </p>
          )}
        </>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Calendar heatmap + timeline</CardTitle>
          <CardDescription>Lands in phase 10.</CardDescription>
        </CardHeader>
      </Card>
    </div>
  );
}

function OverallHero({
  trend,
  loading,
}: {
  trend: TrendForKind | undefined;
  loading: boolean;
}) {
  if (loading || !trend) return <SkeletonCard className="h-32" />;
  const verdict = verdictFor(trend);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Overall</CardTitle>
        <CardDescription>{verdict.headline}</CardDescription>
      </CardHeader>
      <CardContent className="flex items-end justify-between gap-4">
        <div className="flex flex-col">
          <span className="text-4xl font-semibold tabular-nums">{trend.current.count}</span>
          <span className="text-xs text-muted-foreground">events this period</span>
        </div>
        <DeltaPill trend={trend} />
      </CardContent>
    </Card>
  );
}

function KindCard({ trend }: { trend: TrendForKind }) {
  if (trend.kind === 'overall') return null;
  return (
    <Link
      to="/trends/$kind"
      params={{ kind: trend.kind }}
      className="rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <Card className="transition-shadow hover:shadow-md">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <KindIcon kind={trend.kind} className="h-4 w-4" />
            {KIND_LABEL[trend.kind]}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-end justify-between gap-2">
          <div className="flex flex-col">
            <span className="text-2xl font-semibold tabular-nums">{trend.current.count}</span>
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
              vs {trend.prior.count} prior
            </span>
          </div>
          <DeltaPill trend={trend} compact />
        </CardContent>
      </Card>
    </Link>
  );
}

function DeltaPill({ trend, compact = false }: { trend: TrendForKind; compact?: boolean }) {
  const tone = toneFor(trend);
  const label = labelFor(trend);
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium',
        compact ? 'px-2 py-0.5 text-[10px]' : '',
        tone.classes,
      )}
      title={tone.title}
    >
      <span aria-hidden="true">{tone.glyph}</span>
      {label}
    </span>
  );
}

function SkeletonCard({ className }: { className?: string }) {
  return (
    <Card className={cn('animate-pulse', className)}>
      <CardHeader className="pb-2">
        <div className="h-3 w-20 rounded bg-muted" />
      </CardHeader>
      <CardContent>
        <div className="h-6 w-16 rounded bg-muted" />
      </CardContent>
    </Card>
  );
}

function labelFor(trend: TrendForKind): string {
  if (trend.delta_pct == null) {
    return trend.current.count > 0 ? 'new activity' : 'no data';
  }
  const sign = trend.delta_pct > 0 ? '+' : '';
  return `${sign}${Math.round(trend.delta_pct)}%`;
}

function toneFor(trend: TrendForKind): { glyph: string; classes: string; title: string } {
  // Direction is on incident count: up = more symptoms = worse.
  if (trend.delta_pct == null && trend.current.count === 0) {
    return {
      glyph: '·',
      classes: 'border-border text-muted-foreground',
      title: 'no events recorded',
    };
  }
  if (trend.direction === 'up') {
    return {
      glyph: '↑',
      classes: 'border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300',
      title: 'more symptoms than the prior window',
    };
  }
  if (trend.direction === 'down') {
    return {
      glyph: '↓',
      classes:
        'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
      title: 'fewer symptoms than the prior window',
    };
  }
  return {
    glyph: '→',
    classes: 'border-border bg-muted text-muted-foreground',
    title: 'unchanged from the prior window',
  };
}

function verdictFor(trend: TrendForKind): { headline: string } {
  if (trend.current.count === 0 && trend.prior.count === 0) {
    return { headline: 'No incidents logged in either window. Keep it that way.' };
  }
  if (trend.delta_pct == null) {
    return { headline: 'New activity in this window — no prior data to compare.' };
  }
  if (trend.direction === 'up') {
    return {
      headline: `Up ${Math.round(trend.delta_pct)}% vs the prior ${trend.current.count >= trend.prior.count ? 'period' : 'period'}.`,
    };
  }
  if (trend.direction === 'down') {
    return { headline: `Down ${Math.round(Math.abs(trend.delta_pct))}% — trending better.` };
  }
  return { headline: 'Holding steady.' };
}
